/**
 * A post-sign-in destination, kept only when it is a path inside this app.
 *
 * The value arrives in a query string, so it is whatever the link that brought
 * the visitor here says it is. `//host` is read by browsers as another origin,
 * and so is `/\host`: the URL parser treats a backslash as a separator for
 * http(s), which makes `/\evil.com` resolve exactly as `//evil.com` does. Both
 * are refused, along with anything carrying its own scheme.
 *
 * The query string is kept, since a list's filters live there; the fragment is
 * dropped. The sign-in screen itself is refused, or a visitor who just signed
 * in would be sent round again.
 *
 * Shared because it is one rule for both apps, and the narrower of two
 * implementations is the one that lets a crafted link through.
 *
 * @example
 * safeRedirect('/orgs?q=name:contains:acme') // '/orgs?q=name:contains:acme'
 * safeRedirect('//evil.com')                 // undefined
 * safeRedirect('/\\evil.com')                // undefined
 * safeRedirect('/login')                     // undefined
 */
export const safeRedirect = (
  value: unknown,
  { signInPath = '/login' }: { signInPath?: string } = {},
): string | undefined => {
  if (typeof value !== 'string' || !/^\/(?![/\\])/.test(value)) return undefined
  const [withoutFragment = ''] = value.split('#')
  const [path = ''] = withoutFragment.split('?')
  return path === signInPath || path.startsWith(`${signInPath}/`)
    ? undefined
    : withoutFragment
}
